import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button } from '@qwealth/qcore';
import { getPathContents, S3Item } from '@qwealth/qvault';
import { selectPartners, selectUserIsAdmin, selectUserIsPortfolioManager } from '@qwealth/qdata';

import { Kyp, PMCantApproveKypTypeValues } from 'data/models/Kyp';
import { useAppSelector } from 'data/store';
import { selectSelectedKyp } from 'data/selectors/kypSelectors';
import KypDetailsHeader from './KypDetailsHeader';
import KypDetailsTable from './KypDetailsTable';
import TransitionBox from 'components/Households/Common/QNotes/TransitionBox';
import KypModal from '../KypModal/KypModal';
import DocumentPreviewModal from '../KypDocument/DocumentPreviewModal';

const KypDetails: FC = (): JSX.Element => {
  const isUserAdmin = useAppSelector(selectUserIsAdmin);
  const isUserPM = useAppSelector(selectUserIsPortfolioManager);

  const hasEditPermissions = isUserAdmin || isUserPM;
  const partners = useSelector(selectPartners);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<S3Item[]>([]);
  const [attachmentUrl, setAttachmentUrl] = useState<string>('');
  const [attachmentName, setAttachmentName] = useState<string>('');
  const [attachmentType, setAttachmentType] = useState<string>('');
  const kyp = useAppSelector<Kyp | undefined>(selectSelectedKyp);
  const isAdminOnlyKyp = kyp ? kyp.type in PMCantApproveKypTypeValues : false;
  const hasPermissionToApprove = (isAdminOnlyKyp && isUserAdmin) || (!isAdminOnlyKyp && hasEditPermissions);
  useEffect(() => {
    (async () => {
      if (kyp) {
        const attachmentsToSet = await getPathContents(`kyp/${kyp.uuid}/`, true);
        setAttachments(attachmentsToSet);
      } else {
        setAttachments([]);
      }
    })();
  }, [kyp]);

  const clickApprove = () => {
    setShowReviewModal(true);
  };

  const clickEdit = () => {
    setShowEditModal(true);
  };

  const clickCloseModal = () => {
    setShowReviewModal(false);
    setShowEditModal(false);
  };

  const clickAttachment = (url: string, fileName: string, type: string) => {
    setAttachmentUrl(url);
    setAttachmentName(fileName);
    setAttachmentType(type);
    setShowAttachmentModal(true);
  };

  return (
    <Box display="flex" width="100%" height="100%" paddingLeft="largest">
      {kyp && (
        <TransitionBox key={kyp ? kyp.uuid : 'KypDetailsSection'} index={0} isVisible={kyp !== undefined}>
          <KypDetailsHeader kyp={kyp} attachments={attachments} clickEdit={clickEdit} hasEditPermissions={hasEditPermissions} />

          <Box height="90vh" paddingRight="large" overflow="auto" width="100%">
            <KypDetailsTable kyp={kyp} attachments={attachments} clickAttachment={clickAttachment} />

            <Box display="flex" justifyContent="right" width="100%" marginBottom="largest" marginTop="large">
              {hasPermissionToApprove && <Button onClick={clickApprove}>Approve</Button>}
            </Box>
          </Box>
        </TransitionBox>
      )}

      <KypModal
        partners={partners}
        show={showEditModal || showReviewModal}
        onClose={clickCloseModal}
        kyp={kyp}
        type={showEditModal ? 'edit' : 'review'}
      />

      <DocumentPreviewModal
        modalTitle="KYP Attachment"
        url={attachmentUrl}
        fileName={attachmentName}
        type={attachmentType}
        show={showAttachmentModal}
        onClose={() => setShowAttachmentModal(false)}
      />
    </Box>
  );
};

export default KypDetails;
